/**
 * ITER 70 — Convenzioni CRM Dashboard (pagina admin gerarchica).
 *
 * Struttura a 3 livelli:
 *   L0 — Header: selettore partner + periodo + KPI totali
 *   L1 — 3 macro-card (Auto · Famiglia · Tempo Libero) con delta% e barre sub-ramo
 *   L2 — Accordion sub-rami cliccabili
 *   L3 — Tabella richieste paginata + badge N task aperti + link dettaglio
 *
 * Endpoint:
 *   GET /api/admin/convenzioni/crm/config
 *   GET /api/admin/convenzioni/crm/summary?partner=&period=
 *   GET /api/admin/convenzioni/crm/richieste?partner=&macro=&sub=&period=&page=
 *
 * RBAC: super_admin, admin, dipendente (self_scope su drill-down).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, Home, Palmtree, Users, Building2, Calendar as CalIcon,
  RefreshCw, Loader2, ArrowLeft, ArrowUpRight, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, ExternalLink, Filter, Search, ClipboardList,
} from "lucide-react";
import RichiestaRiepilogoAccordion from "@/components/admin/RichiestaRiepilogoAccordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "7d",  label: "7g" },
  { key: "30d", label: "30g" },
  { key: "90d", label: "90g" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

// Map icona-string (dal backend) → componente lucide-react
const ICON_MAP = { car: Car, home: Home, palm: Palmtree };

// Etichette umane partner (per label)
const PARTNER_LABELS = {
  stellantis: "Stellantis",
  leonardo:   "Leonardo",
  gori:       "Gori",
  unicredit:  "Unicredit",
  ge_avio:    "GE Avio",
  hitachi:    "Hitachi",
};
const partnerLabel = (p) => PARTNER_LABELS[p] || (p ? p[0].toUpperCase() + p.slice(1) : "Tutti");

function formatDeltaPct(v) {
  if (v === null || v === undefined) return null;
  const pct = Math.round(v * 1000) / 10; // 1 decimale
  return { pct, positive: v >= 0 };
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════
export default function AdminConvenzioniCrmPage() {
  const navigate = useNavigate();

  // State
  const [config, setConfig] = useState(null);   // {partners, macro_rami, periods}
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState("");
  const [partner, setPartner] = useState("");    // "" = tutti
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [expandedMacro, setExpandedMacro] = useState({ auto: true, famiglia: true, tempo_libero: true });

  // Drill-down state (L3)
  const [drillMacro, setDrillMacro] = useState(null);
  const [drillSub, setDrillSub] = useState(null);
  const [drillItems, setDrillItems] = useState([]);
  const [drillTotal, setDrillTotal] = useState(0);
  const [drillPage, setDrillPage] = useState(1);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState("");
  const [drillStatus, setDrillStatus] = useState("");
  const DRILL_PAGE_SIZE = 25;

  // Fetch config (partners + macro/sub schema)
  useEffect(() => {
    axios.get(`${API}/admin/convenzioni/crm/config`, { withCredentials: true })
      .then(({ data }) => setConfig(data))
      .catch((e) => {
        const msg = e.response?.data?.detail || "Errore caricamento config";
        setError(msg); toast.error(msg);
      });
  }, []);

  // Fetch summary
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true); setError("");
    try {
      const params = { period };
      if (partner) params.partner = partner;
      if (period === "custom") {
        if (!customFrom || !customTo) { setLoadingSummary(false); return; }
        params.custom_from = customFrom; params.custom_to = customTo;
      }
      const { data } = await axios.get(`${API}/admin/convenzioni/crm/summary`,
        { params, withCredentials: true });
      setSummary(data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore caricamento summary";
      setError(msg); toast.error(msg);
    } finally { setLoadingSummary(false); }
  }, [partner, period, customFrom, customTo]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Fetch drill-down
  const loadDrill = useCallback(async (macro, sub, page = 1) => {
    setDrillLoading(true);
    try {
      const params = { period, page, page_size: DRILL_PAGE_SIZE };
      if (partner) params.partner = partner;
      if (macro) params.macro = macro;
      if (sub) params.sub = sub;
      if (drillStatus) params.status = drillStatus;
      if (drillSearch) params.q = drillSearch;
      if (period === "custom") { params.custom_from = customFrom; params.custom_to = customTo; }
      const { data } = await axios.get(`${API}/admin/convenzioni/crm/richieste`,
        { params, withCredentials: true });
      setDrillItems(data.items);
      setDrillTotal(data.total);
      setDrillPage(data.page);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore drill-down";
      toast.error(msg);
    } finally { setDrillLoading(false); }
  }, [partner, period, customFrom, customTo, drillSearch, drillStatus]);

  const openDrill = (macro, sub) => {
    setDrillMacro(macro); setDrillSub(sub);
    setDrillPage(1);
    loadDrill(macro, sub, 1);
    // scroll dopo render
    setTimeout(() => {
      const el = document.querySelector('[data-testid="conv-crm-drill-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };
  const closeDrill = () => { setDrillMacro(null); setDrillSub(null); setDrillItems([]); setDrillTotal(0); };

  // Reload drill when search/status changes (se attivo)
  useEffect(() => {
    if (drillMacro) loadDrill(drillMacro, drillSub, 1);
  }, [drillSearch, drillStatus]);

  const maxMacroCount = useMemo(() => {
    if (!summary?.macro_rami) return 0;
    return Math.max(1, ...summary.macro_rami.map((m) => m.count));
  }, [summary]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30"
         data-testid="admin-convenzioni-crm-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/dashboard")}
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#163321]"
                    data-testid="conv-crm-back-btn">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <div className="h-4 w-px bg-slate-300" />
            <h1 className="font-heading text-lg font-semibold text-[#0F1F14]"
                data-testid="conv-crm-title">
              🎫 Convenzioni CRM
            </h1>
            <span className="text-xs text-slate-500 hidden sm:inline">
              Dashboard gerarchica lead convenzionati
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadSummary} disabled={loadingSummary}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="conv-crm-refresh-btn">
              {loadingSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Aggiorna
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* FASE L1 — Banner promo Cockpit (visione più semplice per operatore) */}
        <div className="mb-4 bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
             data-testid="cockpit-promo-banner">
          <div className="text-sm text-[#0F1F14]">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase mr-1.5">NEW</span>
            <span className="font-semibold">Nuova vista Cockpit disponibile</span>
            <span className="text-slate-600 ml-1">— stream semplificato, azioni inline, filtro Mie/Tutte.</span>
          </div>
          <Link to="/admin/cockpit"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#163321] text-white hover:bg-[#0F1F14] font-semibold"
                data-testid="cockpit-promo-cta">
            Apri Cockpit →
          </Link>
        </div>
        {/* L0 — Filtri */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 shadow-sm"
             data-testid="conv-crm-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            {/* Partner */}
            <div>
              <label className="text-[11px] text-slate-500 font-medium block mb-1">
                Partner (convenzione)
              </label>
              <select
                value={partner}
                onChange={(e) => setPartner(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#163321]"
                data-testid="conv-crm-partner-select"
              >
                <option value="">Tutti i partner</option>
                {(config?.partners || []).map((p) => (
                  <option key={p} value={p}>{partnerLabel(p)}</option>
                ))}
              </select>
            </div>

            {/* Periodo */}
            <div>
              <label className="text-[11px] text-slate-500 font-medium block mb-1">Periodo</label>
              <div className="flex gap-1 flex-wrap" data-testid="conv-crm-period-toggle">
                {PERIODS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all
                      ${period === p.key
                        ? "bg-[#163321] text-white border-[#163321]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#163321]/40"}`}
                    data-testid={`conv-crm-period-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom range (visibile solo se period=custom) */}
            {period === "custom" && (
              <div className="flex items-center gap-2 text-[12px]">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                       className="border border-slate-200 rounded-lg px-2 py-1.5"
                       data-testid="conv-crm-custom-from" />
                <span className="text-slate-400">→</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                       className="border border-slate-200 rounded-lg px-2 py-1.5"
                       data-testid="conv-crm-custom-to" />
              </div>
            )}
          </div>
        </div>

        {/* KPI totali (L0 continua) */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="conv-crm-kpi-strip">
            <KpiTile label="Richieste periodo" value={summary.totals.richieste}
                     icon={ClipboardList} accent="#163321" testid="kpi-tot-richieste" />
            <KpiTile label="Nuove ultimi 7gg" value={summary.totals.nuove_ultimi_7gg}
                     icon={TrendingUp} accent="#10B981" testid="kpi-nuove-7gg" />
            <KpiTile label="Conversion rate"
                     value={summary.totals.richieste ? `${(summary.totals.conversion_rate * 100).toFixed(1)}%` : "—"}
                     icon={ArrowUpRight} accent="#3B82F6" testid="kpi-conv-rate" />
            <KpiTile label="Tempo medio 1° contatto"
                     value={summary.totals.avg_first_contact_days
                            ? `${summary.totals.avg_first_contact_days.toFixed(1)}g` : "—"}
                     icon={CalIcon} accent="#F59E0B" testid="kpi-avg-contact" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg mb-4 text-sm"
               data-testid="conv-crm-error">
            {error}
          </div>
        )}

        {loadingSummary && !summary && (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-8 justify-center"
               data-testid="conv-crm-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento dashboard…
          </div>
        )}

        {/* L1 + L2 — Macro-card accordion */}
        {summary && (
          <div className="space-y-4" data-testid="conv-crm-macro-list">
            {summary.macro_rami.map((m) => (
              <MacroCard
                key={m.id}
                macro={m}
                maxCount={maxMacroCount}
                expanded={!!expandedMacro[m.id]}
                onToggle={() => setExpandedMacro({ ...expandedMacro, [m.id]: !expandedMacro[m.id] })}
                onOpenDrill={openDrill}
                partner={partner}
              />
            ))}
          </div>
        )}

        {/* L3 — Drill-down richieste */}
        {drillMacro && (
          <DrillDownSection
            macro={drillMacro}
            sub={drillSub}
            items={drillItems}
            total={drillTotal}
            page={drillPage}
            pageSize={DRILL_PAGE_SIZE}
            loading={drillLoading}
            search={drillSearch}
            setSearch={setDrillSearch}
            status={drillStatus}
            setStatus={setDrillStatus}
            onPageChange={(p) => loadDrill(drillMacro, drillSub, p)}
            onClose={closeDrill}
            partner={partner}
            period={period}
          />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// KPI Tile
// ═══════════════════════════════════════════════════════════════════════
function KpiTile({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm"
         data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</span>
        <div className="p-1.5 rounded-lg" style={{ background: `${accent}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
      </div>
      <div className="font-heading text-2xl text-[#0F1F14] font-semibold">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Macro-card (L1 + L2 accordion)
// ═══════════════════════════════════════════════════════════════════════
function MacroCard({ macro, maxCount, expanded, onToggle, onOpenDrill, partner }) {
  const Icon = ICON_MAP[macro.icon] || Building2;
  const delta = formatDeltaPct(macro.delta_pct);
  const widthPct = maxCount ? Math.max(4, (macro.count / maxCount) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
         data-testid={`conv-crm-macro-${macro.id}`}>
      {/* Header */}
      <button onClick={onToggle}
              className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-all"
              data-testid={`conv-crm-macro-header-${macro.id}`}>
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2.5 rounded-xl bg-emerald-50">
            <Icon className="w-5 h-5 text-[#163321]" />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-heading text-lg font-semibold text-[#0F1F14]"
                  data-testid={`conv-crm-macro-label-${macro.id}`}>
                {macro.label}
              </h3>
              <span className="text-slate-400 text-sm">·</span>
              <span className="text-2xl font-heading text-[#163321]"
                    data-testid={`conv-crm-macro-count-${macro.id}`}>
                {macro.count}
              </span>
              <span className="text-xs text-slate-500">richieste</span>
              {delta !== null && macro.count > 0 && (
                <span className={`inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded
                  ${delta.positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
                      data-testid={`conv-crm-macro-delta-${macro.id}`}>
                  {delta.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {delta.positive ? "+" : ""}{delta.pct}%
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-2 bg-slate-100 rounded-full h-1.5 overflow-hidden max-w-md">
              <div className="h-full rounded-full transition-all"
                   style={{ width: `${widthPct}%`, background: "linear-gradient(90deg, #163321, #4E9863)" }} />
            </div>
          </div>
        </div>
        {expanded
          ? <ChevronDown className="w-5 h-5 text-slate-400" />
          : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>

      {/* L2 — Sub-rami */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3"
             data-testid={`conv-crm-macro-subs-${macro.id}`}>
          {macro.count === 0 ? (
            <p className="text-xs text-slate-500 italic text-center py-3">
              Nessuna richiesta per questo macro-ramo nel periodo selezionato.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {macro.sub_rami.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onOpenDrill(macro.id, s.id)}
                  disabled={s.count === 0}
                  className={`text-left px-3 py-2.5 rounded-lg border transition-all
                    ${s.count > 0
                      ? "bg-white border-slate-200 hover:border-[#163321] hover:shadow-md"
                      : "bg-white/50 border-slate-100 opacity-60 cursor-not-allowed"}`}
                  data-testid={`conv-crm-sub-${macro.id}-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-medium text-slate-700">{s.label}</span>
                    <span className={`text-lg font-heading font-semibold
                      ${s.count > 0 ? "text-[#163321]" : "text-slate-400"}`}>
                      {s.count}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {macro.count > 0 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-xs">
              <button onClick={() => onOpenDrill(macro.id, null)}
                      className="inline-flex items-center gap-1 text-[#163321] hover:underline"
                      data-testid={`conv-crm-macro-expand-all-${macro.id}`}>
                Espandi tutte le richieste ({macro.count})
                <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// L3 — Drill-down section
// ═══════════════════════════════════════════════════════════════════════
function DrillDownSection({ macro, sub, items, total, page, pageSize, loading, search, setSearch,
                            status, setStatus, onPageChange, onClose, partner, period }) {
  // ITER 71 — Accordion inline: 1 alla volta aperto (chiude precedente al nuovo click)
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const subLabel = items[0]?.sub_label
    || (sub ? sub.replace(/_/g, " ") : "Tutti i sub-rami");
  const macroLabel = macro === "auto" ? "Auto"
    : macro === "famiglia" ? "Famiglia"
    : macro === "tempo_libero" ? "Tempo Libero" : macro;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Link a Kanban (2b — riuso pieno /admin/richieste kanban)
  const kanbanQs = new URLSearchParams();
  if (partner) kanbanQs.set("partner", partner);
  kanbanQs.set("vista", "kanban");
  const kanbanHref = `/admin/richieste?${kanbanQs.toString()}`;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
         data-testid="conv-crm-drill-section">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Drill-down</div>
          <h4 className="font-heading text-base text-[#0F1F14] font-semibold"
              data-testid="conv-crm-drill-title">
            {macroLabel} · {subLabel} <span className="text-slate-400">·</span> <span className="text-[#163321]">{total}</span> richieste
            {partner && <span className="text-slate-500 text-sm"> · {partnerLabel(partner)}</span>}
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <Link to={kanbanHref}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg
                           border border-[#163321] text-[#163321] hover:bg-[#163321] hover:text-white transition-all"
                data-testid="conv-crm-open-kanban-btn">
            <ExternalLink className="w-3.5 h-3.5" />
            Apri in Kanban
          </Link>
          <button onClick={onClose}
                  className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
                  data-testid="conv-crm-drill-close-btn">
            Chiudi
          </button>
        </div>
      </div>

      {/* Filtri drill-down */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center flex-wrap gap-2 bg-slate-50/40">
        <div className="relative flex-1 min-w-[180px] max-w-[320px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Ricerca nome/email/telefono…"
                 className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#163321]"
                 data-testid="conv-crm-drill-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                data-testid="conv-crm-drill-status">
          <option value="">Tutti gli stati</option>
          <option value="nuovo">Nuovo</option>
          <option value="in_lavorazione">In lavorazione</option>
          <option value="preventivato">Preventivato</option>
          <option value="chiuso_positivo">Chiuso positivo</option>
          <option value="chiuso_negativo">Chiuso negativo</option>
        </select>
      </div>

      {/* Tabella richieste */}
      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2"
             data-testid="conv-crm-drill-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="conv-crm-drill-empty">
          Nessuna richiesta trovata con i filtri correnti.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto" data-testid="conv-crm-drill-table-wrap">
            <table className="w-full text-[12px]" data-testid="conv-crm-drill-table">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="w-8 px-2"></th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Data</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Cliente</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Contatti</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Sub-ramo</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Stato</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Task</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <DrillRow key={it.id} item={it}
                              expanded={expandedId === it.id}
                              onToggle={() => toggleExpand(it.id)} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs"
                 data-testid="conv-crm-drill-pagination">
              <span className="text-slate-500">
                Pagina {page} di {totalPages} · {total} richieste totali
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => onPageChange(page - 1)}
                        disabled={page <= 1}
                        className="px-2 py-1 rounded border border-slate-200 disabled:opacity-50 hover:bg-slate-50"
                        data-testid="conv-crm-drill-prev">
                  ‹ Prec
                </button>
                <span className="px-2">{page}</span>
                <button onClick={() => onPageChange(page + 1)}
                        disabled={page >= totalPages}
                        className="px-2 py-1 rounded border border-slate-200 disabled:opacity-50 hover:bg-slate-50"
                        data-testid="conv-crm-drill-next">
                  Succ ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Drill-row (una richiesta nella tabella) — ITER 71 con accordion inline
// ═══════════════════════════════════════════════════════════════════════
function DrillRow({ item, expanded, onToggle }) {
  const dateFmt = item.created_at ? new Date(item.created_at).toLocaleDateString("it-IT", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  }) : "—";
  const emailMask = item.email
    ? item.email.replace(/(.{2}).*(@.*)/, "$1***$2")
    : "—";
  const detailHref = `/admin/richieste?id=${encodeURIComponent(item.id)}`;

  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50/40 cursor-pointer transition-colors
                       ${expanded ? "bg-emerald-50/30" : ""}`}
          onClick={onToggle}
          data-testid={`conv-crm-drill-row-${item.id}`}>
        {/* Chevron expand/collapse */}
        <td className="w-8 px-2 text-slate-400">
          <button className="p-0.5 rounded hover:bg-slate-200"
                  onClick={(e) => { e.stopPropagation(); onToggle(); }}
                  aria-label={expanded ? "Chiudi riepilogo" : "Espandi riepilogo"}
                  data-testid={`conv-crm-drill-expand-${item.id}`}>
            {expanded
              ? <ChevronDown className="w-4 h-4 text-[#163321]" />
              : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-4 py-2 text-slate-700">{dateFmt}</td>
        <td className="px-4 py-2 font-medium text-[#0F1F14]">{item.client_name}</td>
        <td className="px-4 py-2 text-slate-600">
          <div className="text-[11px]">{emailMask}</div>
          <div className="text-[11px] text-slate-400">{item.telefono || "—"}</div>
        </td>
        <td className="px-4 py-2">
          <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-100">
            {item.sub_label || item.sub}
          </span>
        </td>
        <td className="px-4 py-2">
          <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-slate-100 text-slate-700">
            {item.status || "nuovo"}
          </span>
        </td>
        <td className="px-4 py-2">
          {item.open_tasks > 0 ? (
            <Link to={detailHref}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200"
                  data-testid={`conv-crm-drill-tasks-${item.id}`}>
              <ClipboardList className="w-3 h-3" />
              {item.open_tasks} aperti
            </Link>
          ) : (
            <span className="text-[11px] text-slate-400">—</span>
          )}
        </td>
      </tr>
      {/* ITER 71 — Accordion inline: mounted solo se expanded (lazy fetch) */}
      {expanded && (
        <tr data-testid={`conv-crm-drill-row-expanded-${item.id}`}>
          <td colSpan={7} className="p-0">
            <RichiestaRiepilogoAccordion
              richiestaId={item.id}
              onClose={onToggle}
            />
          </td>
        </tr>
      )}
    </>
  );
}
